import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Lock, Store, Tags } from 'lucide-react';
import { useSheinWorkspaceAuth } from '@/shein-platform/context/SheinWorkspaceAuthContext';
import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import SearchableSelect from '@/shein-platform/modules/products/components/SearchableSelect';
import { buildSheinPath, SHEIN_ACTIVE_SHOP_STORAGE_KEY } from '@/shein-platform/constants/sheinRoutes';
import { IPE_WORKBENCH_PATH } from '@/config/ipeRoutes';
import {
  SHEIN_SHOP_TYPE_OPTIONS,
  defaultShopTypeForPlatform,
  normalizeSheinShopType,
  shopTypeEquals,
} from '@/utils/platformShopCatalog';
import {
  listAuthorizedSheinShops,
} from '@/shein-platform/utils/sheinShopAuthGate';
import { gateCopyForShopType } from '@/shein-platform/utils/sheinShopCapabilities';
import { localizedShopTypeLabel } from '@/i18n/shopTypeText';
import {
  PLATFORM_ALL_SHOPS_ID,
  isAllShopsScope,
} from '@/utils/platformShopScope';
import '@/styles/shein-platform/shein-gate.css';

const GATE_HERO = `${process.env.PUBLIC_URL || ''}/shein-gate/hero.png`;
const GATE_ACCENT = `${process.env.PUBLIC_URL || ''}/shein-gate/accent.png`;

function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function readStoredShopId() {
  if (typeof localStorage === 'undefined') return '';
  try {
    return localStorage.getItem(SHEIN_ACTIVE_SHOP_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function persistActiveShopId(shopId) {
  if (typeof localStorage === 'undefined') return;
  try {
    if (shopId) {
      localStorage.setItem(SHEIN_ACTIVE_SHOP_STORAGE_KEY, String(shopId));
    } else {
      localStorage.removeItem(SHEIN_ACTIVE_SHOP_STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

/** 左侧叙事：标题打字 → 导语出现 → 亮点逐条亮起并轮播 */
function useGateStoryMotion(headlineText, highlightCount) {
  const [headline, setHeadline] = useState('');
  const [showLead, setShowLead] = useState(false);
  const [visibleCount, setVisibleCount] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [typingDone, setTypingDone] = useState(false);
  const safeCount = Math.max(1, Number(highlightCount) || 1);
  const text = String(headlineText || '');

  useEffect(() => {
    if (prefersReducedMotion()) {
      setHeadline(text);
      setShowLead(true);
      setVisibleCount(safeCount);
      setTypingDone(true);
      return undefined;
    }

    let cancelled = false;
    let charIndex = 0;
    let typeTimer;
    let leadTimer;
    let revealTimer;
    let cycleTimer;

    setHeadline('');
    setShowLead(false);
    setVisibleCount(0);
    setActiveIndex(0);
    setTypingDone(false);

    const typeNext = () => {
      if (cancelled) return;
      charIndex += 1;
      setHeadline(text.slice(0, charIndex));
      if (charIndex < text.length) {
        typeTimer = window.setTimeout(typeNext, 42);
        return;
      }
      setTypingDone(true);
      leadTimer = window.setTimeout(() => {
        if (cancelled) return;
        setShowLead(true);
        let shown = 0;
        const revealNext = () => {
          if (cancelled) return;
          shown += 1;
          setVisibleCount(shown);
          if (shown < safeCount) {
            revealTimer = window.setTimeout(revealNext, 320);
            return;
          }
          let cursor = 0;
          setActiveIndex(0);
          cycleTimer = window.setInterval(() => {
            cursor = (cursor + 1) % safeCount;
            setActiveIndex(cursor);
          }, 2800);
        };
        revealTimer = window.setTimeout(revealNext, 220);
      }, 280);
    };

    typeTimer = window.setTimeout(typeNext, 380);

    return () => {
      cancelled = true;
      window.clearTimeout(typeTimer);
      window.clearTimeout(leadTimer);
      window.clearTimeout(revealTimer);
      window.clearInterval(cycleTimer);
    };
  }, [text, safeCount]);

  return { headline, showLead, visibleCount, activeIndex, typingDone };
}

function resolveRedirect(searchParams, locationState) {
  const fromQuery = searchParams.get('redirect');
  if (fromQuery) return fromQuery;
  if (locationState?.redirect) return locationState.redirect;
  return buildSheinPath('home');
}

function shopOptionLabel(shop, t) {
  const name = String(shop?.shopName || '').trim() || t('sheinGate.shopFallback', { id: shop?.id });
  const type = localizedShopTypeLabel(t, normalizeSheinShopType(shop?.shopType));
  return type ? t('sheinGate.shopOption', { name, type }) : name;
}

export default function SheinPlatformGatePage() {
  const { t, i18n } = useTranslation('workbench');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { unlocked, checking, unlock, lock, workspaceShopType } = useSheinWorkspaceAuth();
  const [shops, setShops] = useState([]);
  const [shopsLoading, setShopsLoading] = useState(true);
  const [selectedShopType, setSelectedShopType] = useState(() => defaultShopTypeForPlatform('SHEIN'));
  const [selectedShopId, setSelectedShopId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(() => location.state?.shopAuthError || '');
  const [submitting, setSubmitting] = useState(false);

  const preferredShopId = useMemo(
    () => String(searchParams.get('shopId') || '').trim(),
    [searchParams],
  );

  const gateCopy = useMemo(() => {
    void i18n.language;
    return gateCopyForShopType(selectedShopType);
  }, [selectedShopType, i18n.language]);
  const highlightItems = gateCopy.highlights || [];
  const { headline, showLead, visibleCount, activeIndex, typingDone } = useGateStoryMotion(
    gateCopy.headline,
    highlightItems.length,
  );
  const sessionTypeMatches = Boolean(
    unlocked
    && workspaceShopType
    && shopTypeEquals(workspaceShopType, selectedShopType, 'SHEIN'),
  );
  const needsPassword = !sessionTypeMatches;

  const shopTypeSelectOptions = useMemo(
    () => SHEIN_SHOP_TYPE_OPTIONS.map((type) => ({
      value: type,
      label: localizedShopTypeLabel(t, type),
    })),
    [t],
  );

  const shopsOfType = useMemo(
    () => shops.filter((shop) => shopTypeEquals(shop?.shopType, selectedShopType, 'SHEIN')),
    [shops, selectedShopType],
  );

  const shopSelectOptions = useMemo(() => {
    const options = shopsOfType.map((shop) => ({
      value: String(shop.id),
      label: shopOptionLabel(shop, t),
    }));
    if (shopsOfType.length >= 2) {
      return [
        { value: PLATFORM_ALL_SHOPS_ID, label: t('sheinGate.allAuthorized') },
        ...options,
      ];
    }
    return options;
  }, [shopsOfType, t]);

  useEffect(() => {
    if (location.state?.shopAuthError) {
      setError(String(location.state.shopAuthError));
    }
  }, [location.state]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setShopsLoading(true);
      try {
        const list = await listAuthorizedSheinShops();
        if (cancelled) return;
        setShops(list);
        if (!list.length) {
          setSelectedShopId('');
          setError((prev) => prev || t('shop.enterAuthExpired'));
          return;
        }
        // 外链（如店铺管理「进入SHEIN」）指定店铺：优先预填类型与店铺
        const preferred = preferredShopId
          ? list.find((shop) => String(shop.id) === String(preferredShopId))
          : null;
        if (preferred) {
          const type = normalizeSheinShopType(preferred.shopType) || defaultShopTypeForPlatform('SHEIN');
          setSelectedShopType(type);
          setSelectedShopId(String(preferred.id));
          return;
        }
        if (preferredShopId) {
          setError((prev) => prev || t('shop.enterAuthExpired'));
        }
        const stored = readStoredShopId();
        if (isAllShopsScope(stored)) {
          const ofType = list.filter((shop) => shopTypeEquals(shop?.shopType, defaultShopTypeForPlatform('SHEIN'), 'SHEIN'));
          // 若本地是全部，尽量恢复对应类型下的全部入口
          const preferredType = defaultShopTypeForPlatform('SHEIN');
          const preferredShops = list.filter((shop) => shopTypeEquals(shop?.shopType, preferredType, 'SHEIN'));
          if (preferredShops.length >= 2) {
            setSelectedShopType(preferredType);
            setSelectedShopId(PLATFORM_ALL_SHOPS_ID);
            return;
          }
          if (ofType.length >= 2) {
            setSelectedShopType(preferredType);
            setSelectedShopId(PLATFORM_ALL_SHOPS_ID);
            return;
          }
        }
        const storedShop = list.find((shop) => String(shop.id) === String(stored));
        if (storedShop) {
          const type = normalizeSheinShopType(storedShop.shopType) || defaultShopTypeForPlatform('SHEIN');
          setSelectedShopType(type);
          setSelectedShopId(String(storedShop.id));
          return;
        }
        const preferredType = defaultShopTypeForPlatform('SHEIN');
        const preferredShops = list.filter((shop) => shopTypeEquals(shop?.shopType, preferredType, 'SHEIN'));
        if (preferredShops.length === 1) {
          setSelectedShopType(preferredType);
          setSelectedShopId(String(preferredShops[0].id));
        } else if (list.length === 1) {
          const only = list[0];
          setSelectedShopType(normalizeSheinShopType(only.shopType) || preferredType);
          setSelectedShopId(String(only.id));
        } else {
          setSelectedShopType(preferredType);
          setSelectedShopId('');
        }
      } catch {
        if (!cancelled) {
          setShops([]);
          setSelectedShopId('');
          setError(t('shop.enterAuthExpired'));
        }
      } finally {
        if (!cancelled) setShopsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [preferredShopId, t]);

  useEffect(() => {
    if (!selectedShopId) return;
    if (isAllShopsScope(selectedShopId)) {
      if (shopsOfType.length < 2) setSelectedShopId('');
      return;
    }
    const stillVisible = shopsOfType.some((shop) => String(shop.id) === String(selectedShopId));
    if (!stillVisible) setSelectedShopId('');
  }, [shopsOfType, selectedShopId]);

  useEffect(() => {
    if (checking || !unlocked || shopsLoading) return undefined;

    let cancelled = false;
    (async () => {
      try {
        if (!shops.length) {
          await lock();
          if (!cancelled) setError(t('shop.enterAuthExpired'));
          return;
        }

        const preferred = preferredShopId
          ? shops.find((shop) => String(shop.id) === String(preferredShopId))
          : null;

        // 外链指定店铺：会话有效且店铺类型与当前工作区一致时，写入店铺后直达；跨类型需留在本页重验密码
        if (preferred) {
          const typeOk = !workspaceShopType
            || shopTypeEquals(preferred.shopType, workspaceShopType, 'SHEIN');
          if (typeOk) {
            persistActiveShopId(String(preferred.id));
            try {
              await sheinPlatformAPI.updatePreferences({ activeShopId: Number(preferred.id) });
            } catch {
              /* best-effort */
            }
            if (!cancelled) {
              navigate(resolveRedirect(searchParams, location.state), { replace: true });
            }
          }
          return;
        }

        const stored = readStoredShopId();
        const storedOk = isAllShopsScope(stored)
          ? shops.filter((shop) => (
            workspaceShopType
              ? shopTypeEquals(shop.shopType, workspaceShopType, 'SHEIN')
              : true
          )).length >= 2
          : shops.some((shop) => String(shop.id) === String(stored));
        // 会话仍有效且已选过店铺/全部：直接进入；否则留在本页让用户选择店铺
        if (storedOk) {
          if (!cancelled) {
            navigate(resolveRedirect(searchParams, location.state), { replace: true });
          }
        }
      } catch {
        if (!cancelled) {
          setError(t('shop.enterAuthExpired'));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    checking,
    unlocked,
    shopsLoading,
    shops,
    navigate,
    searchParams,
    location.state,
    lock,
    preferredShopId,
    workspaceShopType,
    t,
  ]);

  const applySelectedShop = async (shopId) => {
    const id = String(shopId || '').trim();
    if (!id) return;
    persistActiveShopId(id);
    try {
      if (isAllShopsScope(id)) {
        await sheinPlatformAPI.updatePreferences({
          activeShopId: null,
          preferences: { allShopsMode: true },
        });
      } else {
        await sheinPlatformAPI.updatePreferences({
          activeShopId: Number(id),
          preferences: { allShopsMode: false },
        });
      }
    } catch {
      /* best-effort：本地已写入，进入后上下文会读 localStorage */
    }
  };

  const handleShopTypeChange = (nextType) => {
    setSelectedShopType(nextType);
    setSelectedShopId('');
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (!selectedShopType) {
      setError(t('shop.needShopType'));
      return;
    }
    if (!selectedShopId) {
      setError(t('sheinGate.needShop'));
      return;
    }
    const enteringAll = isAllShopsScope(selectedShopId);
    if (enteringAll) {
      if (shopsOfType.length < 2) {
        setError(t('sheinGate.needTwoShops'));
        return;
      }
    } else {
      const selected = shops.find((shop) => String(shop.id) === String(selectedShopId));
      if (!selected || !shopTypeEquals(selected.shopType, selectedShopType, 'SHEIN')) {
        setError(t('sheinGate.shopUnavailable'));
        return;
      }
    }
    if (needsPassword && !password.trim()) {
      setError(sessionTypeMatches === false && unlocked
        ? t('sheinGate.needPasswordSwitch')
        : t('sheinGate.needPassword'));
      return;
    }
    setSubmitting(true);
    try {
      if (needsPassword) {
        await unlock(password, selectedShopType);
      }
      const list = await listAuthorizedSheinShops();
      if (enteringAll) {
        const stillOk = list.filter(
          (shop) => shopTypeEquals(shop.shopType, selectedShopType, 'SHEIN'),
        ).length >= 2;
        if (!stillOk) {
          await lock();
          setShops(list);
          setSelectedShopId('');
          setError(t('shop.enterAuthExpired'));
          return;
        }
      } else {
        const stillOk = list.some(
          (shop) => String(shop.id) === String(selectedShopId)
            && shopTypeEquals(shop.shopType, selectedShopType, 'SHEIN'),
        );
        if (!stillOk) {
          await lock();
          setShops(list);
          setSelectedShopId('');
          setError(t('shop.enterAuthExpired'));
          return;
        }
      }
      await applySelectedShop(selectedShopId);
      navigate(resolveRedirect(searchParams, location.state), { replace: true });
    } catch (err) {
      const raw = String(err?.message || '');
      const msg = err?.code === 'SHEIN_PASSWORD_INVALID'
        ? t('sheinGate.wrongPassword')
        : (raw || t('sheinGate.passwordFailed'));
      if (/店铺授权/.test(raw)) {
        try {
          await lock();
        } catch {
          /* ignore */
        }
      }
      setError(msg);
      if (needsPassword) {
        setPassword('');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    window.location.href = `${IPE_WORKBENCH_PATH}?activeNav=sheinHub`;
  };

  const submitLabel = submitting
    ? (sessionTypeMatches ? t('sheinGate.entering') : t('sheinGate.verifying'))
    : t('sheinGate.confirmEnter');

  return (
    <div className="shein-gate">
      <div
        className="shein-gate__hero"
        style={{ backgroundImage: `url(${GATE_HERO})` }}
        aria-hidden="true"
      />
      <div className="shein-gate__veil" aria-hidden="true" />

      <div className="shein-gate__stage">
        <section className="shein-gate__story" aria-live="polite">
          <p className="shein-gate__kicker">IPE × SHEIN · {localizedShopTypeLabel(t, selectedShopType || fixedUi("半托管"))}</p>
          <h1 className="shein-gate__brand">IPE</h1>
          <p className={`shein-gate__headline${typingDone ? ' is-done' : ' is-typing'}`}>
            <span>{headline}</span>
            {!typingDone ? <i className="shein-gate__caret" aria-hidden="true" /> : null}
          </p>
          <p className={`shein-gate__lead${showLead ? ' is-visible' : ''}`}>
            {gateCopy.lead}
          </p>

          <ul className="shein-gate__highlights">
            {highlightItems.map((item, index) => {
              const visible = index < visibleCount;
              const active = visible && index === activeIndex && visibleCount >= highlightItems.length;
              return (
                <li
                  key={`${selectedShopType}-${item.title}`}
                  className={`shein-gate__highlight${visible ? ' is-visible' : ''}${active ? ' is-active' : ''}`}
                >
                  <span className="shein-gate__highlight-mark" aria-hidden="true" />
                  <div>
                    <strong>{item.title}</strong>
                    <span>{item.desc}</span>
                  </div>
                </li>
              );
            })}
          </ul>

          <div
            className="shein-gate__accent"
            style={{ backgroundImage: `url(${GATE_ACCENT})` }}
            aria-hidden="true"
          />
        </section>

        <aside className="shein-gate__auth" aria-label={t('sheinGate.authAria')}>
          <div className="shein-gate__auth-glow" aria-hidden="true" />
          <div className="shein-gate__panel">
            <div className="shein-gate__header">
              <span className="shein-gate__brand-badge" aria-hidden="true">SHEIN</span>
              <h2>{t('sheinGate.title')}</h2>
            </div>
            <p className="shein-gate__panel-desc">
              {t('sheinGate.panelLead')}
            </p>

            <form className="shein-gate__form" onSubmit={handleSubmit} autoComplete="off">
              {/* 诱饵字段：承接浏览器账号密码自动填充，避免写入店铺搜索框 */}
              <div className="shein-gate__autofill-trap" aria-hidden="true">
                <input type="text" name="username" autoComplete="username" tabIndex={-1} defaultValue="" />
                <input type="password" name="password" autoComplete="current-password" tabIndex={-1} defaultValue="" />
              </div>
              <div className="shein-gate__field">
                <span>{t('shop.shopType')}</span>
                <div className="shein-gate__input-wrap shein-gate__input-wrap--combobox">
                  <Tags size={16} aria-hidden="true" />
                  <SearchableSelect
                    className="shein-gate__search-select"
                    panelClassName="shein-gate__search-panel"
                    options={shopTypeSelectOptions}
                    value={selectedShopType}
                    onChange={handleShopTypeChange}
                    placeholder={t('sheinGate.shopTypePlaceholder')}
                    disabled={shopsLoading || submitting}
                    allowClear={false}
                    emptyText={t('sheinGate.noShopTypes')}
                    inputType="search"
                    inputName="shein-gate-shop-type"
                    autoComplete="off"
                    antiAutofill
                  />
                </div>
              </div>

              <div className="shein-gate__field">
                <span>{t('sheinGate.loginShop')}</span>
                <div
                  className="shein-gate__input-wrap shein-gate__input-wrap--combobox"
                  onMouseDown={(e) => {
                    if (e.target?.closest?.('.spo-search-select__input')) return;
                    if (e.target?.closest?.('.spo-search-select__clear')) return;
                    const input = e.currentTarget.querySelector('input.spo-search-select__input');
                    if (!input || input.disabled) return;
                    e.preventDefault();
                    input.focus();
                  }}
                >
                  <Store size={16} aria-hidden="true" />
                  <SearchableSelect
                    className="shein-gate__search-select"
                    panelClassName="shein-gate__search-panel"
                    options={shopSelectOptions}
                    value={selectedShopId}
                    onChange={setSelectedShopId}
                    placeholder={shopsLoading ? t('sheinGate.loadingShops') : t('sheinGate.shopSearchPlaceholder')}
                    disabled={shopsLoading || submitting || shops.length === 0}
                    allowClear
                    emptyText={
                      shops.length === 0
                        ? t('sheinGate.noAuthorized')
                        : t('sheinGate.noAuthorizedOfType', { type: localizedShopTypeLabel(t, selectedShopType) })
                    }
                    inputType="search"
                    inputName="shein-gate-shop"
                    autoComplete="off"
                    antiAutofill
                  />
                </div>
              </div>

              {needsPassword ? (
                <label className="shein-gate__field">
                  <span>{t('sheinGate.passwordLabel')}</span>
                  <span className="shein-gate__input-wrap shein-gate__input-wrap--password">
                    <Lock size={16} aria-hidden="true" />
                    <input
                      id="shein-gate-password"
                      name="shein-gate-ipe-verify"
                      className="shein-gate__password-input"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t('sheinGate.passwordPlaceholder')}
                      // 二次验密，不是系统登录；避免浏览器当成账号登录并弹出「保存密码」
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-bwignore="true"
                      data-form-type="other"
                      required
                    />
                    <button
                      type="button"
                      className="shein-gate__password-toggle"
                      onClick={() => setShowPassword((value) => !value)}
                      aria-label={showPassword ? t('sheinGate.hidePassword') : t('sheinGate.showPassword')}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </span>
                </label>
              ) : (
                <p className="shein-gate__session-hint">{t('sheinGate.sessionHint')}</p>
              )}

              {error ? <p className="shein-gate__error">{error}</p> : null}

              <div className="shein-gate__actions">
                <button
                  type="button"
                  className="shein-gate__btn shein-gate__btn--ghost"
                  onClick={handleCancel}
                >
                  {t('sheinGate.backWorkbench')}
                </button>
                <button
                  type="submit"
                  className="shein-gate__btn shein-gate__btn--primary"
                  disabled={submitting || shopsLoading || shops.length === 0}
                >
                  {submitLabel}
                </button>
              </div>
            </form>
          </div>
        </aside>
      </div>
    </div>
  );
}
